import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import PrimaryButton from "../components/PrimaryButton";
import TimelineBar from "../components/TimelineBar";
import { track } from "../utils/analytics";
import { captureCampaign, getCampaignContext } from "../utils/campaign";
import { useForm } from "../context/FormContext";

// The ads lead with "which mahadasha are you in, and when does it change?",
// so the page answers that promise first, for anyone, without assuming who
// the reader is.
const PAGE_VARIANT = "vedic-timeline-v1";

// Visitors from the mahadasha ads (ad-d, ad-si, ad-ta, ad-hi and their photo
// versions) see the word they tapped on; everyone else gets plain English.
// Both get the one-line explanation under the headline.
const MAHADASHA_AD = /^ad-(d|si|ta|hi)(-|$)/;
const HEADLINES = {
  mahadasha: "Which mahadasha are you in, and when does it change?",
  plain: "Which chapter of your life are you in, and when does the next one begin?",
};

export function headlineFor(search) {
  const ad = new URLSearchParams(search).get("utm_content") || "";
  return MAHADASHA_AD.test(ad) ? HEADLINES.mahadasha : HEADLINES.plain;
}

// Keeps the pricing answer honest in both states, since the checkout link is
// baked in at build time.
const CHECKOUT_CONFIGURED = Boolean(process.env.REACT_APP_NEXT_CHAPTER_CHECKOUT_URL);
const FULL_REPORT_SLA = process.env.REACT_APP_FULL_REPORT_SLA || "1 to 2 business days";

// Mirrors the three sections of the generated preview (prompts/kp_preview_system.txt).
const FREE_PREVIEW = [
  { title: "Where you are now", detail: "The life chapter you are in now, the smaller chapter inside it, and when each one ends." },
  { title: "How you are made", detail: "Your strengths and the way you meet life, from your rising sign and your birth star." },
  { title: "What's coming next", detail: "The next chapter linked with the part of life you care about most, and when it starts." },
];

const WHY = [
  { title: "Something has shifted lately.", detail: "And you'd like to know whether your chart marks this stretch as a change of period." },
  { title: "You've heard \"it's your Rahu period\".", detail: "Now you'd like to see your own timeline, with real dates, instead of guessing." },
  { title: "You want to know when the next change comes.", detail: "The month your next major period begins, and how long it lasts." },
];

const QUICK_FACTS = [
  "About two minutes, start to finish",
  "Your dates calculated from your own birth time",
  "Free to read, with nothing to pay",
];

// The same contents the preview page sells (Success.js REPORT_AREAS).
const REPORT_AREAS = [
  "Every sub-period over the next 20 years, with its dates",
  "Career, relationships, money and home: what your chart shows for each",
  "Which of your coming periods your chart links with each of those areas",
  "All 12 areas of your chart read in detail, in plain English",
];

const SAMPLE_PAST = [
  { label: "Partnership", years: "2014–2015", matched: true },
  { label: "Children", years: "2017–2018", matched: true },
  { label: "An ending in a relationship", years: "2009", matched: false },
  { label: "A harder stretch", years: "2010–2013", matched: true },
];

const STEPS = [
  {
    label: "Share your birth details",
    detail: "About two minutes. No account. Have your birth date, time and place ready.",
  },
  {
    label: "We calculate your timeline",
    detail: "Your Vimshottari periods and their dates, from your own sidereal chart, not a generic horoscope.",
  },
  {
    label: "Check it against your past",
    detail: "With a birth time to the minute, we show the years your chart links with partnership, children and harder times, and you tell us if they match. Skip any time.",
  },
  {
    label: "Read your preview",
    detail: "Your timeline and reading appear on screen straight after, and we email you a copy.",
  },
];

const FAQS = [
  {
    q: "Is this Vedic astrology?",
    a: "Yes. Your chart is calculated in the sidereal zodiac used in Vedic (Jyotish) astrology, and your timeline uses the Vimshottari dasha system: the mahadashas and antardashas an astrologer would read. We explain it in plain English, so you don't need to know the terms.",
  },
  {
    q: "I don't know my exact birth time. Can I still do this?",
    a: "Yes. The form asks how sure you are of the time, and we adjust what we read from it. An exact time unlocks the most detail; an approximate or unknown one means we work from the parts of your chart that stay steady and leave the rest out rather than guess. Your birth certificate is worth a look first — even a few minutes changes your period dates.",
  },
  {
    q: "Is this going to predict my future?",
    a: "No. We describe what your chart links each period with, as a lens for reflection. It won't tell you that an event will happen, and it won't tell you whether to leave a relationship, change jobs or make a financial decision.",
  },
  {
    q: "Is AI involved?",
    a: "Yes, and we'd rather say so plainly. Your chart and period dates are calculated astronomically. AI is then used to turn that calculated chart data into a written reflection. We don't send your email address or payment details to the model.",
  },
  {
    q: "What happens to my birth details?",
    a: "Your date, time and place of birth are used to calculate your chart and prepare your reflection. We explain what we collect and why on our Privacy Policy page, and we don't pass your personal details into advertising tools.",
  },
  {
    q: "What does the full report cost?",
    a: CHECKOUT_CONFIGURED
      ? `The preview is free. The complete report is AUD $49 — one payment, no subscription and nothing recurring. It's prepared personally for your chart, so it reaches you by email within ${FULL_REPORT_SLA}. You'll see what it covers before you decide, and if it isn't useful to you, reply within 14 days of delivery for a full refund.`
      : "The preview is free. The optional full report is not available to purchase yet. If it becomes available, you'll see its contents and price before deciding whether to buy.",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [headline] = useState(() => headlineFor(search));
  const { setFormData } = useForm();
  const methodologyRef = useRef(null);
  const heroCtaRef = useRef(null);
  const birthTimeFaqRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const context = captureCampaign({ pageVariant: PAGE_VARIANT, campaignId: "vedic-timeline" });
    track("landing_view", context);
  }, []);

  useEffect(() => {
    function updateStickyCta() {
      const cta = heroCtaRef.current;
      setShowStickyCta(Boolean(cta && cta.getBoundingClientRect().bottom < 0));
    }

    updateStickyCta();
    window.addEventListener("scroll", updateStickyCta, { passive: true });
    document.addEventListener("scroll", updateStickyCta, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateStickyCta);
      document.removeEventListener("scroll", updateStickyCta);
    };
  }, []);

  const start = useCallback(
    (placement) => {
      track("hero_cta_click", { ...getCampaignContext(), cta_placement: placement });
      track("form_start", getCampaignContext());
      setFormData((current) => ({ ...current, landingVariant: PAGE_VARIANT }));
      navigate("/name");
    },
    [navigate, setFormData]
  );

  function showBirthTimeOptions() {
    setOpenFaq(1);
    birthTimeFaqRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function toggleFaq(index) {
    setOpenFaq((current) => (current === index ? null : index));
  }

  return (
    <Background>
      <div className="min-h-screen px-4 py-8 sm:py-10 pb-36">
        <div className="w-full max-w-5xl mx-auto space-y-10">
          <section className="grid items-center gap-6 lg:grid-cols-2 lg:gap-10" aria-labelledby="hero-title">
            <div className="space-y-5 text-center lg:text-left">
              <p className="text-xs font-semibold tracking-widest text-orange-600 uppercase">
                Vedic astrology · Your life-period timeline
              </p>
              <h1 id="hero-title" className="text-3xl sm:text-4xl font-bold leading-tight text-gray-900">
                {headline}
              </h1>
              <p className="text-sm text-gray-700 leading-relaxed">
                Your <strong>mahadasha</strong> is the major planetary period that sets the backdrop
                for years of your life. Most people live through only a handful.
              </p>
              <p className="text-gray-600 leading-relaxed">
                See your major periods and sub-periods with their dates, calculated from your exact
                birth time. Check the years your chart picks out against your own past, then see the
                month your next chapter begins. Explained in plain English.
              </p>
              <div ref={heroCtaRef}>
                <PrimaryButton onClick={() => start("hero")}>See my timeline free</PrimaryButton>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                About 2 minutes · No account · Shown on screen · Reflection, not prediction
              </p>
              <button type="button" onClick={showBirthTimeOptions}
                className="min-h-[44px] px-2 text-sm font-medium text-orange-600 underline focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-2xl">
                Not sure of your exact birth time? See what to know.
              </button>
            </div>

            <PreviewArtifact />
          </section>

          <PhotoBand photo="reader-phone" position="50% 35%" side="left" labelledBy="minutes-title">
            <div className="space-y-4 lg:max-w-md">
              <p className="text-xs font-semibold tracking-widest text-orange-600 uppercase">Made for real life</p>
              <h2 id="minutes-title" className="text-2xl sm:text-3xl font-bold leading-tight text-gray-900">
                A few quiet minutes, on your phone
              </h2>
              <p className="text-gray-700 leading-relaxed">
                No account and nothing to install. Enter your birth details, answer a few optional
                questions, and your timeline appears on screen, with a copy sent to your email.
              </p>
              <ul className="space-y-2.5 text-sm font-medium text-gray-800">
                {QUICK_FACTS.map((fact) => (
                  <li key={fact} className="flex items-start gap-2.5">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-500 text-xs font-bold text-white" aria-hidden="true">✓</span>
                    {fact}
                  </li>
                ))}
              </ul>
              <div className="pt-2 sm:max-w-xs">
                <PrimaryButton onClick={() => start("photo-band")}>See my timeline free</PrimaryButton>
              </div>
            </div>
          </PhotoBand>

          <section className="grid items-center gap-6 lg:grid-cols-2 lg:gap-10" aria-labelledby="past-title">
            <div className="space-y-3 text-center lg:text-left">
              <h2 id="past-title" className="text-xl font-semibold text-gray-900">Check it against your own past first</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Before you read anything about your future, your chart picks out the years it links most
                strongly with partnership, children, endings and harder times. You tell us whether they
                match. Your preview shows you the result, misses included.
              </p>
            </div>
            <SamplePast />
          </section>

          <section aria-labelledby="free-preview-title" className="space-y-4">
            <div className="text-center">
              <h2 id="free-preview-title" className="text-xl font-semibold text-gray-900">Your free preview gives you</h2>
              <p className="mt-1 text-sm text-gray-600">Ready to read on screen, before you decide anything else.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {FREE_PREVIEW.map((item) => (
                <AyupathCard key={item.title}>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.detail}</p>
                  </div>
                </AyupathCard>
              ))}
            </div>
          </section>

          <PhotoBand photo="reader-thoughtful" position="45% 30%" side="right" labelledBy="why-title">
            <div className="space-y-4 lg:max-w-lg">
              <h2 id="why-title" className="text-2xl sm:text-3xl font-bold leading-tight text-gray-900">
                Why people check their timeline
              </h2>
              <ul className="space-y-3">
                {WHY.map((item) => (
                  <li key={item.title} className="rounded-2xl border border-white/70 bg-white/85 p-4 shadow-sm backdrop-blur-sm">
                    <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                    <p className="mt-1 text-sm text-gray-600 leading-relaxed">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          </PhotoBand>

          <section className="space-y-4" aria-labelledby="full-report-title">
            <div className="text-center">
              <h2 id="full-report-title" className="text-xl font-semibold text-gray-900">When you want the whole picture</h2>
              <p className="mt-1 text-sm text-gray-600">
                The preview is free. After reading it, you can choose a complete report built from the same chart.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {REPORT_AREAS.map((item) => (
                <div key={item} className="min-h-16 rounded-2xl border border-gray-200 bg-white/90 p-4 text-sm font-medium text-gray-900">
                  {item}
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-gray-600">
              {CHECKOUT_CONFIGURED
                ? `AUD $49, one payment · Prepared personally and emailed within ${FULL_REPORT_SLA} · 14-day full refund`
                : "Contents and price are shown before any purchase."}
            </p>
          </section>

          <section className="grid gap-6 sm:grid-cols-2" aria-label="How AyuPath works">
            <AyupathCard>
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-gray-900">How it works</h2>
                <ol className="space-y-3">
                  {STEPS.map((step, index) => (
                    <li key={step.label} className="flex items-start gap-3">
                      <span className="h-6 w-6 shrink-0 rounded-2xl bg-orange-100 text-orange-600 text-xs font-semibold grid place-items-center">{index + 1}</span>
                      <div><p className="text-sm font-medium text-gray-900">{step.label}</p><p className="text-sm text-gray-600 leading-relaxed">{step.detail}</p></div>
                    </li>
                  ))}
                </ol>
              </div>
            </AyupathCard>
            <div ref={methodologyRef} className="scroll-mt-24">
              <AyupathCard>
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-gray-900">Personal doesn't have to mean mysterious</h2>
                  <TrustItem title="Your chart" detail="We calculate planetary positions from your birth date, time and place using astronomical ephemeris data, in the sidereal zodiac." />
                  <TrustItem title="Your reflection" detail="AI helps turn that calculated chart into readable reflection." />
                  <TrustItem title="Your privacy" detail="We explain what information is used and how it is handled." />
                  <TrustItem title="Your choice" detail="Astrology is a symbolic reflection tool, not a prediction or instruction about your future." />
                  <button type="button" onClick={() => { track("privacy_summary_view", getCampaignContext()); navigate("/privacy"); }}
                    className="min-h-[44px] w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500">
                    Read the privacy policy
                  </button>
                </div>
              </AyupathCard>
            </div>
          </section>

          <section aria-labelledby="faq-title">
            <AyupathCard>
              <div className="space-y-3">
                <h2 id="faq-title" className="text-sm font-semibold text-gray-900">Questions people ask first</h2>
                <ul className="divide-y divide-gray-200">
                  {FAQS.map((faq, index) => (
                    <li key={faq.q} ref={index === 1 ? birthTimeFaqRef : undefined}>
                      <button type="button" onClick={() => toggleFaq(index)} aria-expanded={openFaq === index}
                        className="flex w-full min-h-[44px] items-center justify-between gap-3 py-3 text-left focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-2xl">
                        <span className="text-sm font-medium text-gray-900">{faq.q}</span>
                        <span className="text-orange-600 text-lg leading-none shrink-0" aria-hidden="true">{openFaq === index ? "−" : "+"}</span>
                      </button>
                      {openFaq === index ? <p className="pb-3 text-sm text-gray-700 leading-relaxed">{faq.a}</p> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </AyupathCard>
          </section>

          <section className="space-y-4 py-4 text-center" aria-labelledby="closing-title">
            <h2 id="closing-title" className="text-xl font-semibold text-gray-900 leading-snug">
              Your next chapter has a start date. See yours.
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">You live through only a handful of major periods. It helps to know which one you're in.</p>
            <div className="mx-auto max-w-md"><PrimaryButton onClick={() => start("footer")}>See my timeline free</PrimaryButton></div>
            <p className="text-xs text-gray-500">Free preview · About 2 minutes · No account · Reflection, not prediction</p>
          </section>
        </div>
      </div>
      {showStickyCta ? (
        <div className="fixed inset-x-0 bottom-20 z-20 mx-auto max-w-md px-4 md:hidden">
          <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm">
            <span className="min-w-0 flex-1 text-xs font-medium text-gray-700">Your life-period timeline · free · ~2 min</span>
            <button type="button" onClick={() => start("sticky")} className="min-h-[44px] shrink-0 rounded-2xl bg-orange-600 px-4 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
              See mine
            </button>
          </div>
        </div>
      ) : null}
    </Background>
  );
}

function PreviewArtifact() {
  return (
    <article className="rounded-3xl border border-gray-200 bg-white shadow-lg" aria-label="Illustrative personal preview">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <span className="text-sm font-semibold text-gray-900">AyuPath · Your preview</span>
        <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">Example</span>
      </div>
      <div className="space-y-5 p-5 sm:p-6">
        <TimelineBar periods={SAMPLE_PERIODS} now={SAMPLE_NOW} />
        <div>
          <p className="text-xs font-semibold uppercase text-orange-600">Where you are now</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">Your Rahu mahadasha has run since February 2009 and ends in <strong>February 2027</strong>, when a 16-year Jupiter mahadasha begins. A person lives through only a handful of these, so this is a real turning point.</p>
        </div>
        <div className="rounded-2xl bg-orange-50 p-4">
          <p className="text-xs font-semibold uppercase text-orange-600">What's coming next</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-900"><strong>Jupiter, February 2027 to February 2043.</strong> Your chart links this stretch with learning, guidance and widening your horizons.</p>
        </div>
        <p className="text-xs leading-relaxed text-gray-500">Illustrative example only. Your preview is calculated from your own chart.</p>
      </div>
    </article>
  );
}

function SamplePast() {
  return (
    <article className="rounded-3xl border border-orange-200 bg-white p-5 shadow-lg space-y-3" aria-label="Illustrative check against the past">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Your chart and your past</p>
          <p className="mt-1 text-base font-semibold text-gray-900">3 of the 4 stretches your chart picked out matched</p>
        </div>
        <span className="shrink-0 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">Example</span>
      </div>
      <ul className="space-y-2 text-sm">
        {SAMPLE_PAST.map((row) => (
          <li key={row.label} className="flex items-center gap-2">
            <span aria-hidden="true"
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-bold ${row.matched ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>
              {row.matched ? "✓" : "–"}
            </span>
            <span className={row.matched ? "text-gray-900" : "text-gray-500"}>
              <strong className="font-medium">{row.label}</strong>, {row.years}
              <span className="sr-only">{row.matched ? ": matched" : ": did not match"}</span>
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

// The sample reader's major periods (an invented chart, the same one the
// preview check uses as S01).
const SAMPLE_NOW = "2026-10-28";
const SAMPLE_PERIODS = [
  { lord: "Mars", start: "2002-02-13", end: "2009-02-13" },
  { lord: "Rahu", start: "2009-02-13", end: "2027-02-13" },
  { lord: "Jupiter", start: "2027-02-13", end: "2043-02-13" },
  { lord: "Saturn", start: "2043-02-13", end: "2062-02-13" },
];

/**
 * A full-width band with a photo that fades into the page. On desktop the
 * photo fills the two-thirds of the band opposite the text (`side` is where
 * the text goes), so the person is never behind the words, and fades only
 * at its inner edge. On phones it fills the top of the band and fades down
 * into the text.
 *
 * The photo fades to transparent (CSS masks in index.css), so the turning
 * background wheel shows through around it. It is decorative (the words
 * carry the meaning), lazy-loaded so it never delays the headline, and WebP
 * with a JPEG fallback.
 */
function PhotoBand({ photo, position, side, labelledBy, children }) {
  const photoSide = side === "left" ? "lg:left-auto lg:right-0" : "lg:right-auto lg:left-0";
  return (
    <section aria-labelledby={labelledBy}
      className="relative left-1/2 w-screen -translate-x-1/2 overflow-hidden">
      <div aria-hidden="true"
        className={`band-photo band-photo--text-${side} absolute inset-x-0 top-0 h-72 sm:h-96 lg:inset-y-0 lg:h-auto lg:w-2/3 ${photoSide}`}>
        <picture>
          <source srcSet={`/images/${photo}.webp`} type="image/webp" />
          <img src={`/images/${photo}.jpg`} alt="" loading="lazy" decoding="async"
            className="h-full w-full object-cover" style={{ objectPosition: position }} />
        </picture>
      </div>
      <div className="relative mx-auto max-w-5xl px-4 pt-64 pb-12 sm:pt-80 lg:py-24">
        <div className={side === "right" ? "lg:ml-auto lg:w-5/12" : "lg:w-5/12"}>{children}</div>
      </div>
    </section>
  );
}

function TrustItem({ title, detail }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
    </div>
  );
}
